<template>
  <header class="c-header" :class="headerStyle">
    <div class="c-header_container">
      <div class="c-header_wrapper">
        <div class="c-header_menu">
          <component :is="activeMenu" :dark-mode="isDarkMode" />
        </div>

        <div class="c-header_popup">
          <transition :name="desktopTransition">
            <component :is="activeDesktop" :key="`popup-${selectedName}`" />
          </transition>
        </div>

        <div class="c-header_slideout">
          <transition :name="mobileTransition">
            <component :is="activeMobile" :key="`slideout-${selectedName}`" />
          </transition>
        </div>
      </div>
    </div>

    <transition name="s-fade">
      <div class="c-header_backdrop" v-show="activeDesktop || activeMobile" @click="closeAll"></div>
    </transition>
  </header>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { landing, landingSupplier, quoteRequest } from 'routes'
import { parse as parseUrl } from 'url'
import { isWithinFirstAccessDate, setFirstAcess } from 'Services/firstAccessManager'
import firstAccessTypes from 'Enums/firstAccessTypes'
import menuConversion from 'Components/Header/Menu/menuConversion.vue'
const menuExternal = () => import('Components/Header/Menu/menuExternal.vue')
const menuCompany = () => import('Components/Header/Menu/menuCompany.vue')
const menuUser = () => import('Components/Header/Menu/menuUser.vue')
const menuUserDashboard = () => import('Components/Header/Menu/menuUserDashboard.vue')

const dropdownSales = () => import('Components/Header/Dropdown/dropdownSales.vue')
const dropdownShopping = () => import('Components/Header/Dropdown/dropdownShopping.vue')

const loadPopupCategory = () => import('Components/Load/loadPopupCategory.vue')
const loadPopupAccount = () => import('Components/Load/loadPopupAccount.vue')

const loadSlideoutAccount = () => import('Components/Load/loadSlideoutAccount.vue')
const loadSlideoutCategory = () => import('Components/Load/loadSlideoutCategory.vue')

const initialState = () => ({
  conversionPageList: [
    landing.index,
    landingSupplier.index
  ],

  userDashboardPageList: [
    quoteRequest.index,
  ],
})

export default {
  components: {
    menuCompany,
    menuConversion,
    menuExternal,
    menuUser,
    menuUserDashboard
  },

  created() {
    this.getGroupList()
  },

  mounted() {
    const hourRange = 2
    const isFirstAccess = isWithinFirstAccessDate(firstAccessTypes.migratedUser, hourRange)

    if (isFirstAccess === null) {
      setFirstAcess(firstAccessTypes.migratedUser)
    }
  },

  data () {
    return initialState()
  },

  computed: {
    ...mapState({
      isAuthenticated: state => state.account.authenticated,
      isCompanyActive: state => state.account.company.isActive,
      selected: state => state.navigation.selected,
    }),

    isDarkMode() {
      return this.isAuthenticated && this.isCompanyActive
    },

    isConversionPage() {
      const { pathname } = parseUrl(location.href)

      return this.conversionPageList.some(url => pathname === url)
    },

    isUserDashboardPage() {
      const { pathname } = parseUrl(location.href)

      return this.userDashboardPageList.some(url => pathname === url)
    },

    headerStyle() {
      const darkStyle = this.isDarkMode
        ? 'c-header--darkMode'
        : ''

      const conversionMode = this.isConversionPage && !this.isAuthenticated
        ? 'c-header--conversion'
        : ''

      return [darkStyle, conversionMode].join(' ')
    },

    activeMenu() {
      if (this.isUserDashboardPage && !this.isAuthenticated) {
        return 'menu-user-dashboard';
      }

      const getInternalMenu = (isCompany) => isCompany
        ? 'menu-company'
        : 'menu-user'

      const getExternalMenu = (isConversion) => isConversion
        ? 'menu-conversion'
        : 'menu-external'

      const getComponent = (isConversion, isCompany) => this.isAuthenticated
        ? getInternalMenu(isCompany)
        : getExternalMenu(isConversion)


      return getComponent(this.isConversionPage, this.isCompanyActive)
    },

    selectedName() {
      let name = null
      for (let [key, value] of Object.entries(this.selected)) {
        if (value) {
          name = key
          break
        }
      }

      return name
    },

    activeDesktop() {
      const getComponent = key => ({
        'category': () => ({
          component: import('Components/Header/Popup/popupCategory.vue'),
          loading: loadPopupCategory
        }),
        'user': () => ({
          component: import('Components/Header/Popup/popupAccount.vue'),
          loading: loadPopupAccount
        }),
        'company': () => ({
          component: import('Components/Header/Popup/popupAccount.vue'),
          loading: loadPopupAccount
        }),
        'sales': dropdownSales,
        'shopping': dropdownShopping,
      }[key])

      return getComponent(this.selectedName)
    },

    activeMobile() {
      const getComponent = key => ({
        'category': () => ({
          component: import('Components/Header/Slideout/slideoutCategory.vue'),
          loading: loadSlideoutCategory,
        }),
        'user': () => ({
          component: import('Components/Header/Slideout/slideoutAccount.vue'),
          loading: loadSlideoutAccount,
        }),
        'company': () => ({
          component: import('Components/Header/Slideout/slideoutAccount.vue'),
          loading: loadSlideoutAccount,
        }),
        'sales': dropdownSales,
        'shopping': dropdownShopping,
      }[key])

      return getComponent(this.selectedName)
    },

    desktopTransition() {
      const getComponent = key => ({
        'category': 's-fade',
        'user': 's-fade',
        'company': 's-fade',
        'sales': 's-fade',
        'shopping': 's-fade',
      }[key])

      return getComponent(this.selectedName)
    },

    mobileTransition() {
      const getComponent = key => ({
        'category': 's-fade',
        'user': 's-fade',
        'company': 's-fade',
        'sales': 's-fade',
        'shopping': 's-fade',
      }[key])

      return getComponent(this.selectedName)
    },
  },

  methods: {
    ...mapActions({
      toggleMenu: 'navigation/toggleMenu',
      closeAll: 'navigation/closeAll',
      getGroupList: 'navigation/getGroupList',
    }),
  }
}
</script>

<style lang="scss">
@import "~css/_references";
@import '~css/Modules/menu';
@import '~css/States/hide';

.c-header {
  position: relative;
  z-index: 3;
  box-shadow: 0 1px 5px rgba($v-colorBlack, 0.1);

  &--darkMode {
    background: $v-colorBlack;

    .c-popupAccount {
      display: grid;
      grid-template-columns: 2fr 1fr;
    }

    .c-popupAccount_panelUser {
      border-left: 1px solid $v-colorGray-light-1;
    }

    .c-header_container {
      background: $v-colorBlack;
    }

    .c-headerLogo {
      border-color: rgba($v-colorGray-dark-1, .6);

      &::after {
        color: $v-colorWhite;
        background-color: lighten($v-colorBlack, 5%);
      }

      @media (min-width: #{$v-screen-mdl + 40px}) {
        padding-right: $v-gap-sm;
      }
    }
  }

  &--conversion {
    @include f-media('>=desktop') {
      .c-header_container {
        max-height: 120px;
      }

      .c-header_wrapper {
        height: 120px;
      }

      .c-header_popup {
        top: 120px;
      }
    }
  }

  &_container {
    position: relative;
    width: 100%;
    height: 100%;
    z-index: 12;
    background: $v-colorWhite;
    max-height: 60px;

    @include f-media('>=desktop') {
      max-height: 70px;
    }
  }

  &_wrapper {
    position: relative;
    display: flex;
    flex-flow: row nowrap;
    align-content: center;
    justify-content: center;
    height: 60px;
    max-width: $v-screen-container;
    margin: 0 auto;
    z-index: 13;

    @include f-media('>=desktop') {
      height: 70px;
    }
  }

  &_backdrop {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    height: 100%;
    z-index: 11;
    background: rgba($v-colorBlack, 0.4);
    cursor: pointer;

    @supports (backdrop-filter: blur(3px)) {
      background: rgba($v-colorBlack, 0.1);
      backdrop-filter: brightness(78%) blur(3px);
    }
  }

  &_menu {
    display: flex;
    flex-flow: row nowrap;
    align-items: flex-start;
    flex-grow: 1;
    width: 100%;
  }

  &_slideout {
    @extend .s-hideStarting-md;
    position: absolute;
    left: 0;
    right: 0;
    z-index: 12;
  }

  &_popup {
    @extend .s-hideUntil-md;
    position: absolute;
    top: 60px;
    z-index: 12;
    left: 0;
    right: 0;

    @include f-media('>=desktop') {
      top: 70px;
    }
  }
}

.l-headerContainer {
  & .c-headerPublisher + .c-header .c-headerLogo_snet {
    display: none;
  }

  & .c-headerPublisher.c-headerPublisher--hidden + .c-header .c-headerLogo_snet {
    display: block;

    @include f-media('<desktop') {
      display: none;
    }
  }
}
</style>
